import React, { useLayoutEffect, useRef } from 'react'
import { Box, ButtonBase, Skeleton, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { IconSeeker } from '@ui-seeker/core'

import PageWrapper from 'Pages/@PageWrapper'
import { AppResultsSkeleton } from '@components'
import { dsColorNew, dataColors } from '@palette'
import { mbScopeSx } from './scope'

export { mbScopeSx, mbSectionBarSx, mbLegendBottom, mbCompact, MB_CHART_COLORS } from './scope'


// A My Business page: the app's page wrapper with the module's widgets in the app's look
export function MbPage({ children, sx = {}, ...props }) {
	return <PageWrapper {...props}>
		<Box sx={{ ...mbScopeSx, ...sx }}>{children}</Box>
	</PageWrapper>
}

// A change against the comparison as a soft pill: green up, red down, grey flat
export function MbDelta({ value, suffix = '%', label, sx = {} }) {
	if (value == null) return null
	const tone = value > 0 ? 'up' : value < 0 ? 'down' : 'flat'
	const colors = {
		up: [dsColorNew.successBg, dsColorNew.successText],
		down: [dsColorNew.dangerBg, dsColorNew.dangerRed],
		flat: [dsColorNew.pillTrackBg, dsColorNew.textMuted],
	}[tone]

	return <Box component="span" sx={{
		display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.25, borderRadius: '10px',
		backgroundColor: colors[0], color: colors[1], fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', lineHeight: 1.5, ...sx,
	}}>
		<IconSeeker icon={tone === 'up' ? 'arrow-up' : tone === 'down' ? 'arrow-down' : 'minus'} sx={{ fontSize: 9 }}/>
		{label ?? `${value > 0 ? '+' : ''}${value}${suffix}`}
	</Box>
}

// A figure card as the Main Page ones: icon tile, label in small capitals, the figure, then what it is compared with.
// With onSelect it is a switch: the selected card gets the app blue border and a check.
// `action` (CU-869fkdhjv phase 3, UX C1): one small control in the top-right corner — "Ask Iris" — with the
// same room the check takes; without it the card is exactly as before. Not combined with `onSelect`.
export function MbKpiCard({ icon, color = dsColorNew.webBlue500, label, value, delta, deltaLabel, compareValue, hint, selected, onSelect, action, children, sx = {} }) {
	const selectable = typeof onSelect === 'function'
	const hasAction = !selectable && !!action
	const body = <>
		{hasAction && <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>{action}</Box>}
		{selectable && <Box sx={{
			position: 'absolute', top: 12, right: 12, width: 16, height: 16, borderRadius: '50%',
			display: 'flex', alignItems: 'center', justifyContent: 'center',
			border: `1.5px solid ${selected ? dsColorNew.appBlue : dataColors.gray300}`,
			backgroundColor: selected ? dsColorNew.appBlue : dsColorNew.white,
		}}>
			{selected && <IconSeeker icon="check" sx={{ fontSize: 8, color: dsColorNew.white }}/>}
		</Box>}
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, pr: selectable || hasAction ? 2.5 : 0 }}>
			{!!icon && <Box className="kpi-icon" sx={{
				width: 32, height: 32, borderRadius: '9px', flexShrink: 0,
				display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: alpha(color, 0.12),
			}}>
				<IconSeeker icon={icon} sx={{ color, fontSize: 14 }}/>
			</Box>}
			<Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', color: dsColorNew.textMuted, lineHeight: 1.35 }}>
				{label}
			</Typography>
		</Box>
		<Typography component="div" className="kpi-value" sx={{ fontSize: 26, fontWeight: 700, lineHeight: 1.15, mt: 1.25, color: dsColorNew.textTitle, wordBreak: 'break-word' }}>
			{value}
		</Typography>
		{(delta != null || compareValue != null) && <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: 1.25 }}>
			<MbDelta value={delta ?? 0} label={delta == null ? '—' : deltaLabel}/>
			{compareValue != null && <Typography component="span" sx={{ fontSize: 12.5, color: dsColorNew.textMuted }}>vs {compareValue}</Typography>}
		</Box>}
		{children}
		<Typography sx={{ fontSize: 12, color: dsColorNew.textSubtle, mt: 'auto', pt: 0.75, minHeight: '1.4em' }}>{hint || ' '}</Typography>
	</>
	const cardSx = {
		position: 'relative', width: 1, height: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch', textAlign: 'left',
		p: '16px 18px', borderRadius: '16px', backgroundColor: dsColorNew.white,
		border: `1px solid ${dsColorNew.grayBorder}`,
		boxShadow: selected ? `0 0 0 3px ${dsColorNew.pillActiveBg}` : `0 1px 2px ${alpha(dsColorNew.navy, 0.04)}`,
		transition: 'box-shadow .18s, border-color .18s, transform .18s',
		...(selected && { borderColor: dsColorNew.appBlue }),
		containerType: 'inline-size',
		// A narrow card shrinks the figure instead of cutting it
		'@container (max-width: 190px)': { '& .kpi-value': { fontSize: 21 } },
		...sx,
	}

	if (!selectable) return <Box sx={cardSx}>{body}</Box>

	return <ButtonBase onClick={() => onSelect()} aria-pressed={!!selected} sx={{
		...cardSx,
		'&:hover': { borderColor: selected ? dsColorNew.appBlue : dsColorNew.webBlue500, transform: 'translateY(-2px)' },
		'&.Mui-focusVisible': { outline: `2px solid ${dsColorNew.webBlue500}`, outlineOffset: 2 },
	}}>
		{body}
	</ButtonBase>
}

// The row of figure cards: as many columns as cards, wrapping on small screens
export function MbKpiRow({ children, min = 190, sx = {} }) {
	return <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`, gap: 2, ...sx }}>{children}</Box>
}

const bar = (width, height = 10) => <Skeleton variant="rounded" width={width} height={height} sx={{ borderRadius: '5px' }}/>

// The figure cards while they load: icon tile, label, figure and the change pill
export function MbKpiSkeleton({ count = 4, min = 190 }) {
	return <MbKpiRow min={min}>
		{Array.from({ length: count }, (_, i) => <Box key={i} aria-busy="true" sx={{
			p: '16px 18px', borderRadius: '16px', backgroundColor: dsColorNew.white, border: `1px solid ${dsColorNew.grayBorder}`,
		}}>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
				<Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: '9px' }}/>
				{bar('45%')}
			</Box>
			<Box sx={{ mt: 1.75 }}>{bar('65%', 22)}</Box>
			<Box sx={{ mt: 1.5 }}>{bar('40%', 16)}</Box>
		</Box>)}
	</MbKpiRow>
}

// A card with only rows of text while it loads (lists, office rows)
export function MbListSkeleton({ rows = 6, rowHeight = 18, sx = {} }) {
	return <Box aria-busy="true" sx={{ backgroundColor: dsColorNew.white, border: `1px solid ${dsColorNew.grayBorder}`, borderRadius: '16px', p: 2.5, ...sx }}>
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
			<Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: '8px' }}/>
			{bar(180, 14)}
		</Box>
		{Array.from({ length: rows }, (_, i) => <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '60px 1fr 2fr 90px 60px', gap: 2, alignItems: 'center', py: 1 }}>
			{bar('100%', rowHeight > 14 ? 12 : 10)}{bar('80%')}{bar('100%', 8)}{bar('100%')}{bar('70%')}
		</Box>)}
	</Box>
}

/**
 * The page while its data loads, in the shape of what is coming:
 *   kpis   number of figure cards
 *   tiles  number of small cards under them (Revenue buckets)
 *   blocks in order: 'chart' | 'table' | 'split' (two lists side by side) | 'tabs' | 'list' | a number (chart height)
 */
export function MbPageSkeleton({ kpis = 0, tiles = 0, blocks = [] }) {
	return <Box aria-busy="true" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
		{kpis > 0 && <MbKpiSkeleton count={kpis}/>}
		{tiles > 0 && <Box sx={{ mt: -1 }}><MbKpiRow>{Array.from({ length: tiles }, (_, i) => <Skeleton key={i} variant="rounded" height={84} sx={{ borderRadius: '12px' }}/>)}</MbKpiRow></Box>}
		{blocks.map((block, i) => {
			if (block === 'table') return <AppResultsSkeleton key={i} rows={6}/>
			if (block === 'tabs')  return <Skeleton key={i} variant="rounded" width={420} height={44} sx={{ borderRadius: '12px' }}/>
			if (block === 'list')  return <MbListSkeleton key={i}/>
			if (block === 'split') return <Box key={i} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
				<MbListSkeleton/><MbListSkeleton/>
			</Box>
			return <AppResultsSkeleton key={i} chart height={typeof block === 'number' ? block : 340}/>
		})}
	</Box>
}

// A widget that reloads on its own keeps its height while its skeleton shows, so the page below does not jump:
//   const [cardRef, heldHeight] = useHeldHeight()  →  <Paper ref={cardRef}> … and the skeleton gets height: heldHeight
export function useHeldHeight() {
	const ref = useRef(null)
	const height = useRef(0)
	useLayoutEffect(() => { if (ref.current) height.current = ref.current.offsetHeight })
	return [ref, height.current]
}

// The skeleton sx for that held height (nothing on the first load)
export const heldHeightSx = (height) => height ? { height, overflow: 'hidden', boxSizing: 'border-box' } : {}
